import {
  useCallback, useContext, useEffect, useMemo, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { TranslationContext } from "Components/Generic/Translator";
import { IItem } from "Model/Storage/IItem";
import { getName, updateAllItems, updateProjectItem } from "Utils/Item";
import { duplicateCheckCacheSet, projectsUpdateStart } from "Store/Actions";
import { selectDuplicateCheckCache, selectDuplicateCheckResults } from "Store/Selectors/Omikron";
import { IDuplicateCheckResult } from "Model/Omikron/IDuplicateCheckResult";
import { IProject } from "Model/Storage/IProject";
import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { IUsePages as IUseDuplicateCheck } from "./IUseDuplicateCheck";
import { Content } from "../Content";
import { MergeContent } from "./MergeContent";
import { ValidateContent } from "./ValidateContent";
import { ProjectItemContext } from "../ProjectItemContext/ProjectItemContext";
import { useProject } from "../Hooks/UseProject";

export function useDuplicateCheck(): IUseDuplicateCheck {
  const dispatch = useDispatch();
  const { preflightAuthentication } = useAuthenticationPreflight();
  const translator = useContext(TranslationContext);
  const { projectId, projectItemId, isOriginSelected } = useContext(ProjectItemContext);
  const dcResults = useSelector(selectDuplicateCheckResults);
  const project = useProject(projectId);
  const cache = useSelector(selectDuplicateCheckCache);
  const [selectedPage, setSelectedPage] = useState(0);

  const results = useMemo(
    () => dcResults.map((resultList) => resultList.map((result) => ({
      ...result,
      "@resultTitle": getName(result),
      "@resultBadge": translator.get(`dc.hitcolumn.${result.HitColumn}`, `Hit Column ${result.HitColumn}`)
    }))),
    [dcResults, translator]
  );

  useEffect(() => {
    setSelectedPage(0);
    dispatch(
      duplicateCheckCacheSet(updateProjectItem(cache ?? project, projectItemId, (item) => ({ ...item, "@isDC": true })))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectItemId]);

  const onIsRelevant = useCallback(
    (pItemId: string) => {
      dispatch(
        duplicateCheckCacheSet(
          updateProjectItem(cache ?? project, pItemId, (item: IItem) => ({
            ...item,
            "@isRelevant": !item[`@isRelevant`]
          }))
        )
      );
    },
    [cache, dispatch, project]
  );

  const onNextPage = useCallback(() => {
    setSelectedPage((s) => s + 1);
  }, []);

  const onPreviousPage = useCallback(() => {
    setSelectedPage((s) => s - 1);
  }, []);

  const onSelect = useCallback(
    (result: IDuplicateCheckResult) => {
      dispatch(duplicateCheckCacheSet(applyResult(cache ?? project, result)));
    },
    [cache, dispatch, project]
  );

  const pages = useMemo(
    () => [
      {
        header: translator.get(`dc.merge.select`, `Select duplicate`),
        contentFn: () => (
          <Content
            results={results}
            cache={cache}
            onSelect={onSelect}
            onAction={onNextPage}
            onIsRelevant={onIsRelevant}
          />
        )
      },
      {
        disabled: isOriginSelected,
        header: translator.get(`dc.merge.merge`, `Merge duplicate`),
        contentFn: () => <MergeContent onPrevious={onPreviousPage} onValidate={onNextPage} />
      },
      {
        disabled: selectedPage < 1,
        header: translator.get(`dc.merge.validate`, `Validate`),
        contentFn: () => <ValidateContent onPrevious={onPreviousPage} />
      }
    ],
    [translator, isOriginSelected, selectedPage, results, cache, onSelect, onNextPage, onIsRelevant, onPreviousPage]
  );

  const onSave = useCallback(async () => {
    if (cache == null) {
      return;
    }

    await preflightAuthentication();
    dispatch(projectsUpdateStart([updateAllItems(cache, (item) => ({ ...item, "@isDC": true }))]));
    dispatch(duplicateCheckCacheSet(null));
  }, [cache, dispatch, preflightAuthentication]);

  return {
    pages,
    selectedPage,
    onSave
  };
}

function applyResult(project: IProject, result: IDuplicateCheckResult): IProject {
  return updateProjectItem(project, result[`@id`], (item) => ({
    ...item,
    "@isDC": true
  }));
}
